import { DatePicker } from "./DatePicker";
import { TagPicker } from "./TagPicker";
import { TaskDependenciesPicker } from "./TaskDependenciesPicker";
import { PropertyChip } from "./PropertyChip";
import { createsDependencyCycle, type DependencySelection } from "@/lib/taskDependencies";
import { ApprovalPicker, NO_APPROVAL } from "./Approval";
import { useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { unwrap } from "@tern/api-client";
import { GitBranch, X } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { liveTasks, useProjectStore, type ProjectData } from "@/lib/store";
import { TASK_STATUSES } from "@/lib/tasks";
import { toast } from "@/lib/toast";
import { Picker } from "./Picker";
import { Avatar } from "./PropertyIcons";
import { TaskStatusIcon } from "./TaskProps";
import { MarkdownEditor } from "./Markdown";

export type TaskDefaults = { status?: string; parent_id?: string; lead_id?: string | null };

export function TaskCreateDialog({ d, open, onOpenChange, defaults }: { d: ProjectData; open: boolean; onOpenChange: (o: boolean) => void; defaults?: TaskDefaults }) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        {open && <Form d={d} onOpenChange={onOpenChange} defaults={defaults} />}
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Form({ d, onOpenChange, defaults }: { d: ProjectData; onOpenChange: (o: boolean) => void; defaults?: TaskDefaults }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [summary, setSummary] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState(defaults?.status ?? "backlog");
  const [lead, setLead] = useState<string | null>(defaults?.lead_id ?? null);
  const [parent, setParent] = useState<string | null>(defaults?.parent_id ?? null);
  const [start, setStart] = useState("");
  const [target, setTarget] = useState("");
  const [labels, setLabels] = useState<string[]>([]);
  const [dependencies, setDependencies] = useState<DependencySelection>({ predecessor_ids: [], successor_ids: [] });
  const submitting = useRef(false);
  const [busy, setBusy] = useState(false);
  const [approval, setApproval] = useState(NO_APPROVAL);
  const [editorBusy, setEditorBusy] = useState(false);
  const [tagBusy, setTagBusy] = useState(false);
  const leadUser = lead ? d.users[lead] : undefined;
  const invalidSchedule = !!(start && target && target < start);

  const submit = async () => {
    if (!name.trim() || submitting.current || editorBusy || tagBusy || invalidSchedule) return;
    if (createsDependencyCycle(Object.values(d.dependencies), dependencies)) { toast(t("planning.cycle"), { tone: "error" }); return; }
    submitting.current = true;
    setBusy(true);
    try {
      const task = unwrap(
        await api.POST("/api/workspaces/{slug}/projects/{key}/tasks", {
          params: { path: { slug: d.slug, key: d.project.key } },
          body: { ...approval, ...dependencies, label_ids: labels, name: name.trim(), summary: summary || null, description: description || null, status, lead_id: lead, milestone_id: null, parent_id: parent, start_date: start || null, target_date: target || null },
        }),
      );
      useProjectStore.getState().applyTask(task);
      if (dependencies.predecessor_ids.length || dependencies.successor_ids.length) await useProjectStore.getState().refreshDelta(d.project.id);
      toast(t("task.created", { name: task.name }), { action: { label: t("common.open"), run: () => void navigate({ to: "/$ws/task/$id", params: { ws: d.slug, id: task.id } }) } });
      onOpenChange(false);
    } catch (e) {
      toast(errorMessage(e), { tone: "error" });
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  return (
    <Dialog.Content
      className="fixed left-1/2 top-[12dvh] z-50 flex max-h-[calc(88dvh-24px)] w-[880px] max-w-[calc(100vw-32px)] -translate-x-1/2 flex-col overflow-hidden rounded-2xl border border-border bg-elevated shadow-2xl"
      onKeyDown={(e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
          e.preventDefault();
          void submit();
        }
      }}
    >
      <div className="flex shrink-0 items-center gap-2 px-4 pt-3 pb-2 text-[12px] text-muted">
        <span className="rounded border border-border px-1.5 py-px">{d.project.key}</span>
        <span>›</span>
        <Dialog.Title className="font-medium">{parent ? t("task.newSubTask") : t("task.newTask")}</Dialog.Title>
        <Dialog.Close className="ml-auto rounded p-1 hover:bg-hover"><X className="h-4 w-4" /></Dialog.Close>
      </div>
      <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-y-contain">
        <div className="px-6 pt-3">
          <input autoFocus className="w-full bg-transparent text-[22px] font-semibold text-fg outline-none placeholder:text-faint" placeholder={t("task.namePlaceholder")} value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void submit(); } }} />
          <input className="mt-2 w-full bg-transparent text-[13px] text-fg outline-none placeholder:text-faint" placeholder={t("task.summaryPlaceholder")} value={summary} onChange={(e) => setSummary(e.target.value)} />
        </div>
        <fieldset disabled={busy} className="flex min-w-0 flex-wrap items-center gap-2 px-6 py-5">
          <ApprovalPicker d={d} value={approval} onChange={(config) => { setApproval(config); if (config.approval_required && status === "completed") setStatus("done"); }} disabled={busy} />
          <Picker value={status} items={TASK_STATUSES.filter((s) => !approval.approval_required || s !== "completed").map((s) => ({ id: s, label: t(`taskStatus.${s}`), icon: <TaskStatusIcon status={s} /> }))} onSelect={setStatus}
            trigger={<PropertyChip><TaskStatusIcon status={status} /> {t(`taskStatus.${status}`)}</PropertyChip>} />
          <Picker value={lead ?? "none"} items={[{ id: "none", label: t("task.noLead"), icon: <Avatar size={16} /> }, ...Object.values(d.users).map((u) => ({ id: u.id, label: u.name, icon: <Avatar user={u} size={16} /> }))]} onSelect={(v) => setLead(v === "none" ? null : v)}
            trigger={<PropertyChip><Avatar user={leadUser} size={16} /> {leadUser?.name ?? t("task.lead")}</PropertyChip>} />
          <DatePicker value={start} onChange={setStart} label={t("task.startDate")} boundary="start" max={target} disabled={busy} />
          <DatePicker value={target} onChange={setTarget} label={t("task.targetDate")} boundary="end" min={start} disabled={busy} />
          <TagPicker d={d} value={labels} onChange={setLabels} onBusyChange={setTagBusy} disabled={busy} />
          <TaskDependenciesPicker d={d} value={dependencies} onChange={setDependencies} disabled={busy} />
          <Picker value={parent ?? "none"} items={[{ id: "none", label: t("task.noParent") }, ...liveTasks(d).map((x) => ({ id: x.id, label: x.name, icon: <TaskStatusIcon status={x.status} className="h-3 w-3" /> }))]} onSelect={(v) => setParent(v === "none" ? null : v)}
            trigger={<PropertyChip><GitBranch className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{parent ? d.tasks[parent]?.name : t("task.parent")}</span></PropertyChip>} />
        </fieldset>
        <div className="min-w-0 border-t border-border px-6 py-4"><MarkdownEditor onBusyChange={setEditorBusy} projectId={d.project.id} value={description} onChange={setDescription} onSave={() => void submit()} disabled={busy} placeholder={t("task.descriptionPlaceholder")} minRows={5} /></div>
      </div>
      <div className="flex shrink-0 items-center justify-end border-t border-border px-4 py-2.5">
        <button className="btn btn-primary h-8" disabled={!name.trim() || busy || editorBusy || tagBusy || invalidSchedule} onClick={() => void submit()}>
          {t("task.create")} <kbd className="kbd ml-1">⌘↵</kbd>
        </button>
      </div>
    </Dialog.Content>
  );
}
